import { call, put, select } from "redux-saga/effects";
import { selectUser } from "../../features/auth/authSlice";
import { User } from "../../types/User";
import {
  setError as setDashError,
  setStatus as setDashStatus,
  setProjectTaskInputs,
  setProjectTaskOutputs,
  setProjectTasks,
  setProjects,
} from "../../features/library/librarySlice";
import * as actions from "../actions";

import {
  apiCreateProject,
  apiCreateProjectTask,
  apiCreateProjectTaskInput,
  apiDeleteProjectTaskInput,
  apiGetProjectTask,
  apiGetProjectTasks,
  apiGetProjects,
  apiToggleProjectTaskInput,
  apiUpdateProject,
  apiUpdateProjectTask,
  apiUpdateProjectTaskInput,
} from "../../services/ProjectAPI";

export function* createProject(
  action: ReturnType<typeof actions.createProject>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiCreateProject,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload
    );

    yield put(actions.getProjects());
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* getProjects() {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    const response = yield call(
      apiGetProjects,
      userState.default_organization_id,
      userState.default_workspace_id
    );

    yield put(setProjects(response.data.projects ?? []));

    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* updateProject(
  action: ReturnType<typeof actions.updateProject>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiUpdateProject,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.projectUpdateRequest
    );

    yield put(actions.getProjects());
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* getProjectTasks(
  action: ReturnType<typeof actions.getProjectTasks>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    const response = yield call(
      apiGetProjectTasks,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload
    );
    const data = response.data;
    const tasks = data?.tasks ?? (Array.isArray(data) ? data : []);
    yield put(setProjectTasks(tasks));

    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* createProjectTask(
  action: ReturnType<typeof actions.createProjectTask>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiCreateProjectTask,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.projectTaskCreateRequest
    );

    yield put(actions.getProjectTasks(action.payload.projectId));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* updateProjectTask(
  action: ReturnType<typeof actions.updateProjectTask>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiUpdateProjectTask,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.taskId,
      action.payload.projectTaskUpdateRequest
    );

    yield put(actions.getProjectTasks(action.payload.projectId));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* getProjectTask(
  action: ReturnType<typeof actions.getProjectTask>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    const response = yield call(
      apiGetProjectTask,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.taskId
    );
    const data = response.data ?? {};
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? [];

    console.log("inputs", inputs);

    yield put(setProjectTaskInputs(inputs));
    yield put(setProjectTaskOutputs(outputs));
    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* createProjectTaskInput(
  action: ReturnType<typeof actions.createProjectTaskInput>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiCreateProjectTaskInput,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.taskId,
      action.payload.projectTaskInputCreateRequest
    );

    // Refresh current task inputs/outputs
    yield put(
      actions.getProjectTask({
        projectId: action.payload.projectId,
        taskId: action.payload.taskId,
      })
    );
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* updateProjectTaskInput(
  action: ReturnType<typeof actions.updateProjectTaskInput>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiUpdateProjectTaskInput,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.taskId,
      action.payload.inputId,
      action.payload.projectTaskInputCreateRequest
    );

    // Refresh current task inputs/outputs
    yield put(
      actions.getProjectTask({
        projectId: action.payload.projectId,
        taskId: action.payload.taskId,
      })
    );
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* toggleProjectTaskInput(
  action: ReturnType<typeof actions.toggleProjectTaskInput>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiToggleProjectTaskInput,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.taskId,
      action.payload.inputId,
      action.payload.to_process
    );

    yield put(
      actions.getProjectTask({
        projectId: action.payload.projectId,
        taskId: action.payload.taskId,
      })
    );
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* deleteProjectTaskInput(
  action: ReturnType<typeof actions.deleteProjectTaskInput>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      apiDeleteProjectTaskInput,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.projectId,
      action.payload.taskId,
      action.payload.inputId
    );

    yield put(
      actions.getProjectTask({
        projectId: action.payload.projectId,
        taskId: action.payload.taskId,
      })
    );
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}
